import { FileInfo, createFileId, getFileExtension, idFactory } from "@zickko/shared";
import { useState } from "react";
import { clientConfig } from "../config/clientConfig";
import { writeFileToGcs } from "../gcs/writeFileToGcs";
import { asyncOperation } from "../utils/asyncOperation";
import { toastStore } from "../utils/toastStore";

type Props = {
  gcsBucket: string;
  gcsFolder: string;
  onUploadComplete: (fileInfo: FileInfo) => void;
  allowedFiles: string[];
  dataCy?: string;
};

export const FileInput = (props: Props) => {
  //
  const [progress, setProgress] = useState(0);

  const uploadToGcs = async (file: File) => {
    //
    if (clientConfig.isCypress) {
      console.log(`Skipping file upload for cypress test run`);
      props.onUploadComplete({
        id: idFactory.createId(),
        path: "users/cypress_uid/images/placeholder.png",
        name: "placeholder.png",
      });
      return;
    }

    const { gcsBucket, gcsFolder, allowedFiles } = props;

    const filename = file.name || "";
    const extension = getFileExtension(filename);
    const fileId = createFileId({ extension });
    const path = `${gcsFolder}/${fileId}`;

    const isAllowed = allowedFiles.includes(extension);

    if (!isAllowed) {
      toastStore.error(`Please choose another file with the allowed file extension: ${allowedFiles.join(", ")}.`);
      return;
    }

    await asyncOperation({
      fn: () =>
        writeFileToGcs({
          file,
          path,
          bucket: gcsBucket,
          onProgress: (percentage: number) => setProgress(percentage),
        }),
      loadingMessage: `Uploading ${filename}`,
      onSuccess: () =>
        props.onUploadComplete({
          id: idFactory.createId(),
          path,
          name: filename,
        }),
    });
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const files = event.target.files;
    if (!files) return;
    if (files.length !== 1) {
      toastStore.error(`Cannot handle multiple files.`);
      return;
    }
    uploadToGcs(files.item(0)!);
  };

  const renderProgressBar = () => {
    if (progress <= 0 || progress >= 100) {
      return null;
    }
    return (
      <div>
        <progress max="100" value={progress}>
          {progress}
        </progress>
      </div>
    );
  };

  return (
    <div className="fileinput">
      <input className="file-input" type="file" onChange={onChange} />
      {renderProgressBar()}
    </div>
  );
};
